import type { Kitchen } from '@rta/rta-blueprint-library';

import type { FlatTrimGroup } from './types';
import { getDimensionMeasurement } from '@r-core/helpers';
import { getFlatTrimName } from './get-flat-trim-name';

/** Dedupes every flat trim in the design onto its view-frame `leftHeight-rightHeight` pair, tallest first */
export const getFlatTrimGroups = (kitchen: Kitchen): Array<FlatTrimGroup> => {
	const groups: Map<string, FlatTrimGroup> = new Map();
	const countedIds: Set<string> = new Set();

	kitchen.design.islands.children.forEach((island) => {
		(island.panelTrims?.children ?? []).forEach((panelTrim) => {
			if (!panelTrim.isFlat) return;

			// a seam's two halves are one physical piece, so count the pair once
			if (countedIds.has(panelTrim.id)) return;
			countedIds.add(panelTrim.id);
			const paired = panelTrim.pairedSeamTrim;
			if (paired) countedIds.add(paired.id);

			const { width, halfWidth, nearHeight, farHeight, isStepped, alignment } = panelTrim.flatTrimDimensions;

			// alignment names the taller side in the view frame, so left/right is the only frame the physical piece exists in
			const tall = Math.max(nearHeight, farHeight);
			const short = Math.min(nearHeight, farHeight);
			const leftHeight = alignment === 'right' ? short : tall;
			const rightHeight = alignment === 'left' ? short : tall;

			const key = `${getDimensionMeasurement(leftHeight, false)}-${getDimensionMeasurement(rightHeight, false)}`;
			const existing = groups.get(key);

			if (existing) {
				existing.count++;
				return;
			}

			groups.set(key, {
				key,
				id: panelTrim.id,
				count: 1,
				width,
				halfWidth,
				leftHeight,
				rightHeight,
				isStepped,
				name: getFlatTrimName(leftHeight, rightHeight, isStepped)
			});
		});
	});

	return Array.from(groups.values())
		.sort((a, b) => Math.max(b.leftHeight, b.rightHeight) - Math.max(a.leftHeight, a.rightHeight));
};
